import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Snowflake, ShieldCheck, Heart, Star, ArrowRight, MapPin, Sparkles } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";

const ICONS = { Sparkles, Snowflake, Truck, ShieldCheck, Heart };
const ICON_FALLBACK = Sparkles;

export default function Home() {
  const { settings } = useSettings();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, p, r] = await Promise.all([
        api.get("/categories"),
        api.get("/products?featured=true&limit=6"),
        api.get("/reviews?homepage=true"),
      ]);
      setCategories(c.data);
      setFeatured(p.data);
      setReviews(r.data);
    })().catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A192F] text-white">
        <div className="absolute inset-0">
          {settings.hero_image_url && (
            <img
              src={settings.hero_image_url}
              className="w-full h-full object-cover opacity-50"
              alt="Tekne ve gün doğumu"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/80 to-transparent" />
          <div className="absolute inset-0 hero-grain opacity-50" />
        </div>
        <div className="relative container-max px-4 md:px-8 py-24 md:py-36">
          <div className="max-w-2xl fade-up">
            <div className="overline text-[#E9C46A] mb-5">Çiftlikten Sofranıza</div>
            <h1 className="font-display text-5xl md:text-7xl font-black leading-[0.95] tracking-tighter">
              {settings.hero_title}
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-xl leading-relaxed">
              {settings.hero_subtitle}
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/urunler" className="btn-primary" data-testid="hero-products-cta">
                {settings.hero_cta_primary_label} <ArrowRight size={18} />
              </Link>
              <a
                href={settings.contact_whatsapp ? `https://wa.me/${settings.contact_whatsapp}` : "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/30 px-7 py-3 text-white font-semibold hover:bg-white hover:text-[#0A192F] transition-all duration-300"
                data-testid="hero-order-cta"
              >
                {settings.hero_cta_secondary_label}
              </a>
            </div>
            <div className="flex flex-wrap gap-6 mt-10 text-sm text-slate-300">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400" /> 12.000+ memnun müşteri</span>
              <span className="flex items-center gap-2"><Star size={14} className="text-[#E9C46A] fill-[#E9C46A]" /> 4.9 / 5.0 değerlendirme</span>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-[#0077B6] text-white py-3 overflow-hidden">
        <div className="flex gap-12 animate-marquee whitespace-nowrap text-sm font-semibold tracking-wide">
          {[..."•••••••"].map((_, i) => (
            <span key={i} className="flex items-center gap-3">
              GÜNLÜK TAZE HASAT <span className="opacity-50">◆</span>
              OTOBÜS İLE HIZLI GÖNDERİM <span className="opacity-50">◆</span>
              SOĞUK ZİNCİR TESLİMAT <span className="opacity-50">◆</span>
              TÜM OTOGARLAR <span className="opacity-50">◆</span>
            </span>
          ))}
        </div>
      </div>

      {/* CATEGORIES */}
      <section className="section-pad bg-white">
        <div className="container-max">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline mb-2">Ürün Kategorilerimiz</div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight">
                Boyuna Göre <span className="text-[#0077B6]">Seçin</span>
              </h2>
            </div>
            <Link to="/urunler" className="btn-ghost" data-testid="see-all-products">
              Tümünü Gör <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((c, idx) => (
              <Link
                key={c.id}
                to={`/urunler/${c.slug}`}
                className={`group relative overflow-hidden rounded-2xl bg-[#F4F7F9] aspect-[4/5] fade-up-${idx + 1}`}
                data-testid={`category-card-${c.slug}`}
              >
                <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/60 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                  <div className="overline text-[#E9C46A] mb-2">Kategori #{c.order}</div>
                  <h3 className="font-display text-3xl font-extrabold tracking-tight mb-2">{c.name}</h3>
                  <p className="text-sm text-slate-200 mb-4 leading-relaxed">{c.description}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold border-b-2 border-[#E9C46A] pb-0.5">
                    Hemen Keşfet <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section-pad bg-[#F4F7F9]">
        <div className="container-max">
          <div className="text-center mb-12">
            <div className="overline mb-2">Öne Çıkan Ürünler</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight">
              Bu Hafta <span className="text-[#0077B6]">En Çok Tercih Edilenler</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="section-pad bg-white">
        <div className="container-max">
          <div className="text-center mb-12">
            <div className="overline mb-2">Neden Midye Merkezi?</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight">
              Güven Veren <span className="text-[#0077B6]">5 Adımda</span> Tazelik
            </h2>
          </div>
          <div className="grid md:grid-cols-12 gap-5">
            {(settings.features || []).map((f, i) => {
              const Icon = ICONS[f.icon] || ICON_FALLBACK;
              const span = i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-4" : i === 3 ? "md:col-span-4" : "md:col-span-4";
              return (
                <div key={i} className={`${span} bg-[#F4F7F9] rounded-2xl p-8 border border-slate-200 hover:border-[#0077B6]/30 transition-colors`}>
                  <div className="w-12 h-12 rounded-xl bg-[#0077B6]/10 flex items-center justify-center text-[#0077B6] mb-5">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl font-bold mb-2">{f.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-pad bg-[#0A192F] text-white">
        <div className="container-max">
          <div className="text-center mb-12">
            <div className="overline text-[#E9C46A] mb-2">Müşteri Yorumları</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight">
              Lezzeti Yaşayanlar <span className="text-[#E9C46A]">Anlatıyor</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10">
                <div className="flex gap-1 mb-3">
                  {[...Array(r.rating)].map((_, i) => <Star key={i} size={14} className="text-[#E9C46A] fill-[#E9C46A]" />)}
                </div>
                <p className="text-slate-100 mb-5 leading-relaxed text-sm">"{r.comment}"</p>
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-8 h-8 rounded-full bg-[#0077B6] flex items-center justify-center font-bold">{r.name[0]}</div>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-slate-400 flex items-center gap-1"><MapPin size={10} /> {r.city || "Türkiye"}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-white">
        <div className="container-max grid md:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/3]">
            <img src={settings.about_image_url} alt="Çiftlik" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="overline mb-3">Hakkımızda</div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mb-5">
              {settings.about_title}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              {settings.about_content}
            </p>
            <Link to="/hakkimizda" className="btn-primary" data-testid="about-link">
              Hikayemizi Okuyun <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
